// Copyright 2022 Carnegie Mellon University. All Rights Reserved.
// Released under a MIT (SEI)-style license. See LICENSE.md in the project root for license information.


import { Component, Inject, OnDestroy, OnInit, ViewChild, DOCUMENT } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, Observable } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import {
  ComnAuthService,
  ComnSettingsService,
  Theme,
  ComnAuthQuery,
} from '@cmusei/crucible-common';
import { UserDataService } from 'src/app/data/user/user-data.service';
import { UserQuery } from 'src/app/data/user/user.query';
import { CurrentUserQuery } from 'src/app/data/user/user.query';
import { PermissionDataService } from 'src/app/data/permission/permission-data.service';
import { SystemPermission } from 'src/app/generated/api';
import { TopbarView } from './../shared/top-bar/topbar.models';
import {
  ApplicationArea,
  SignalRService,
} from 'src/app/services/signalr.service';
import {
  Collection,
  Exhibit,
  Team,
  User,
} from 'src/app/generated/api/model/models';
import { CollectionDataService } from 'src/app/data/collection/collection-data.service';
import { CollectionQuery } from 'src/app/data/collection/collection.query';
import { UserArticleDataService } from 'src/app/data/user-article/user-article-data.service';
import { CardDataService } from 'src/app/data/card/card-data.service';
import { ExhibitDataService } from 'src/app/data/exhibit/exhibit-data.service';
import { ExhibitQuery } from 'src/app/data/exhibit/exhibit.query';
import { TeamDataService } from 'src/app/data/team/team-data.service';
import { TeamQuery } from 'src/app/data/team/team.query';
import { TeamCardDataService } from 'src/app/data/team-card/team-card-data.service';
import { UIDataService } from 'src/app/data/ui/ui-data.service';
import { Section } from 'src/app/utilities/enumerations';
import { XApiService } from 'src/app/services/xapi/xapi.service';

@Component({
  selector: 'app-home-app',
  templateUrl: './home-app.component.html',
  styleUrls: ['./home-app.component.scss'],
  standalone: false
})
export class HomeAppComponent implements OnDestroy, OnInit {
  @ViewChild('sidenav') sidenav: MatSidenav;
  @ViewChild(MatSort) set sort(sort: MatSort) {
    if (sort) {
      this.exhibitList.sortingDataAccessor = (exhibit, column) => {
        switch (column) {
          case 'collection': return this.getCollectionName(exhibit.collectionId) || '';
          case 'dateCreated': return exhibit.dateCreated ? new Date(exhibit.dateCreated).toISOString() : '';
          default: return exhibit[column as keyof Exhibit] as string;
        }
      };
      this.exhibitList.sort = sort;
    }
  }
  @ViewChild(MatPaginator) set paginator(paginator: MatPaginator) {
    if (paginator) {
      this.exhibitList.paginator = paginator;
    }
  }
  apiMessage =
    'The GALLERY API web service appears to be experiencing technical difficulties.';
  titleText = 'Gallery';
  exhibitId = '';
  exhibit: Exhibit;
  collection: Collection;
  currentMove = -1;
  currentInject = -1;
  selectedSection: string = Section.none;
  loggedInUserId = '';
  userList: User[] = [];
  collectionList: Collection[] = [];
  collectionLoadCount = 0;
  allExhibits: Exhibit[] = [];
  exhibitList = new MatTableDataSource<Exhibit>();
  teamList: Team[] = [];
  selectedTeamId = '';
  isAuthorizedUser = false;
  isSidebarOpen = true;
  private unsubscribe$ = new Subject();
  hideTopbar = false;
  topbarImage = this.settingsService.settings.AppTopBarImage;
  TopbarView = TopbarView;
  theme$: Observable<Theme>;
  public filterString: string;
  username = '';
  permissions: SystemPermission[] = [];
  canViewAdministration = false;
  readonly SystemPermission = SystemPermission;
  readonly Section = Section;

  get canManageCurrentExhibit(): boolean {
    if (!this.exhibitId) return false;
    return this.permissionDataService.canManageExhibit(this.exhibitId);
  }

  constructor(
    @Inject(DOCUMENT) private _document: HTMLDocument,
    private authService: ComnAuthService,
    private userDataService: UserDataService,
    private userQuery: UserQuery,
    private currentUserQuery: CurrentUserQuery,
    private permissionDataService: PermissionDataService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private settingsService: ComnSettingsService,
    private authQuery: ComnAuthQuery,
    private signalRService: SignalRService,
    private userArticleDataService: UserArticleDataService,
    private cardDataService: CardDataService,
    private exhibitDataService: ExhibitDataService,
    private exhibitQuery: ExhibitQuery,
    private teamDataService: TeamDataService,
    private teamQuery: TeamQuery,
    private teamCardDataService: TeamCardDataService,
    private collectionDataService: CollectionDataService,
    private collectionQuery: CollectionQuery,
    private uiDataService: UIDataService,
    private xApiService: XApiService
  ) {
    this.theme$ = this.authQuery.userTheme$;
    this.hideTopbar = this.inIframe();
    // Set the display settings from config file
    this.titleText = this.settingsService.settings.AppTopBarText;
  }

  ngOnInit() {
    this.currentUserQuery
      .select()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((cu) => {
        if (cu.name) {
          this.username = cu.name;
          this.isAuthorizedUser = !!cu.id;
          this.loggedInUserId = cu.id;
          this.startup();
        }
      });
    this.userDataService.setCurrentUser();
    this.permissionDataService
      .load()
      .subscribe(
        (x) => {
          this.permissions = this.permissionDataService.permissions;
          this.canViewAdministration = this.permissionDataService.canViewAdministration();
        }
      );
    this.permissionDataService.loadExhibitPermissions().subscribe();
  }

  startup() {
    // load collections
    this.collectionDataService.loadMine();
    // subscribe to route changes
    this.activatedRoute.queryParamMap
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((params) => {
        // exhibit and collection
        const exhibitId = params.get('exhibit');
        if (exhibitId) {
          this.exhibitId = exhibitId;
          this.exhibitDataService.loadById(exhibitId);
          this.exhibitDataService.setActive(exhibitId);
          this.uiDataService.setExhibit(exhibitId);
          // section
          this.selectedSection = params.get('section') as Section;
          if (this.selectedSection) {
            this.uiDataService.setSection(exhibitId, this.selectedSection);
          } else {
            this.selectedSection = this.uiDataService.getSection(exhibitId);
          }
          if (!this.selectedSection) {
            this.selectedSection = Section.archive;
          }
          this.loadExhibitData();
        } else {
          this.exhibitId = '';
          this.exhibitDataService.setActive('');
          this.collectionDataService.setActive('');
          this.exhibitDataService.loadMine();
          this._document.getElementById('appTitle').innerHTML =
            this.settingsService.settings.AppTitle;
        }
        // card
        const cardId = params.get('card');
        if (exhibitId && cardId) {
          this.cardDataService.setActive(cardId);
        }
        // team
        if (this.exhibitId && this.selectedTeamId) {
          this.uiDataService.setTeam(exhibitId, this.selectedTeamId);
          const myTeamId = this.teamDataService.getMyTeamId();
          if (this.selectedTeamId === myTeamId) {
            // Viewing own team
            if (this.selectedSection === Section.archive) {
              this.xApiService
                .viewedExhibitArchive(this.exhibitId)
                .pipe(take(1))
                .subscribe();
              if (cardId && cardId !== 'all') {
                this.xApiService
                  .viewedCard(this.exhibitId, cardId)
                  .pipe(take(1))
                  .subscribe();
              }
            } else if (this.selectedSection === Section.wall) {
              this.xApiService
                .viewedExhibitWall(this.exhibitId)
                .pipe(take(1))
                .subscribe();
            }
          } else if (myTeamId) {
            // Observing another team
            if (this.selectedSection === Section.archive) {
              this.xApiService
                .observedExhibitArchive(this.exhibitId, this.selectedTeamId)
                .pipe(take(1))
                .subscribe();
            } else if (this.selectedSection === Section.wall) {
              this.xApiService
                .observedExhibitWall(this.exhibitId, this.selectedTeamId)
                .pipe(take(1))
                .subscribe();
            }
          }
        }
      });
    // subscribe to the collections
    this.collectionQuery
      .selectAll()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((collections) => {
        this.collectionList = collections;
      });
    // subscribe to the exhibits
    this.exhibitQuery
      .selectAll()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((exhibits) => {
        this.allExhibits = exhibits;
        this.applyFilter(this.filterString);
      });
    // subscribe to the active exhibit
    (this.exhibitQuery.selectActive() as Observable<Exhibit>)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((exhibit) => {
        if (exhibit && exhibit.id) {
          this.exhibit = exhibit;
          // only handle the changing of move or inject if they are both defined and have actually changed
          const inIntialState =
            this.currentMove === -1 || this.currentInject === -1;
          const moveOrInjectChanged =
            !inIntialState &&
            (this.exhibit.currentMove !== this.currentMove ||
              this.exhibit.currentInject !== this.currentInject);
          this.currentMove = this.exhibit.currentMove;
          this.currentInject = this.exhibit.currentInject;
          if (moveOrInjectChanged) {
            this.handleExhibitMoveOrInjectChange();
          }
        }
      });
    // subscribe to the user list
    this.userQuery.selectAll()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((users) => {
        this.userList = users;
      });
    this.userDataService.load().pipe(take(1)).subscribe();
    // subscribe to teams
    this.teamQuery
      .selectAll()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((teams) => {
        // in the initial state, we need to set the team and register with signalr.
        if (!this.selectedTeamId) {
          this.teamList = teams;
          this.setMyTeam();
        }
      });
    this.signalRService
      .startConnection(ApplicationArea.home)
      .then(() => {
        this.signalRService.join();
      })
      .catch((err) => {
        console.log(err);
      });
    this.filterString = '';
  }

  setMyTeam() {
    let myTeamId = '';
    this.teamList.forEach((t) => {
      t.users.forEach((u) => {
        if (u && u.id === this.loggedInUserId) {
          myTeamId = t.id;
          this.teamDataService.setMyTeam(t.id);
        }
      });
    });
    if (!this.teamQuery.getActiveId() || !this.selectedTeamId) {
      this.changeTeam(myTeamId);
      this.uiDataService.setTeam(this.exhibitId, myTeamId);
    }
  }

  changeTeam(teamId: string) {
    let oldTeamId = this.selectedTeamId;
    if (oldTeamId !== teamId) {
      oldTeamId = oldTeamId ? oldTeamId : teamId;
      this.signalRService.switchTeam(oldTeamId, teamId);
    }
    this.selectedTeamId = teamId;
    this.teamDataService.setActive(teamId);
    this.uiDataService.setTeam(this.exhibitId, teamId);
    this.cardDataService.setActive('');
    this.loadTeamData();
    const myTeamId = this.teamDataService.getMyTeamId();
    if (myTeamId && this.selectedTeamId && this.exhibitId && this.selectedTeamId !== myTeamId) {
      if (this.selectedSection === Section.archive) {
        this.xApiService
          .observedExhibitArchive(this.exhibitId, this.selectedTeamId)
          .pipe(take(1))
          .subscribe();
      } else if (this.selectedSection === Section.wall) {
        this.xApiService
          .observedExhibitWall(this.exhibitId, this.selectedTeamId)
          .pipe(take(1))
          .subscribe();
      }
    }
  }

  logout() {
    this.authService.logout();
  }

  inIframe() {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  }

  loadExhibitData() {
    // process the change
    this.exhibitDataService.setActive(this.exhibitId);
    this.teamDataService.loadMine(this.exhibitId);
  }

  loadTeamData() {
    // process the change
    if (this.exhibitId && this.selectedTeamId) {
      this.cardDataService.loadByExhibitTeam(this.exhibitId, this.selectedTeamId);
      this.cardDataService.setActive('all');
      this.teamCardDataService.loadByExhibitTeam(
        this.exhibitId,
        this.selectedTeamId
      );
      this.userArticleDataService.loadByExhibitTeam(
        this.exhibitId,
        this.selectedTeamId
      );
    }
  }

  getCollectionName(collectionId: string) {
    return this.collectionList.find(c => c.id === collectionId)?.name;
  }

  handleExhibitMoveOrInjectChange() {
    if (!this.exhibitId) return;
    this.teamCardDataService.loadByExhibitTeam(
      this.exhibitId,
      this.selectedTeamId
    );
    this.userArticleDataService.loadByExhibitTeam(
      this.exhibitId,
      this.selectedTeamId
    );
  }

  getUserName(userId: string) {
    const user = this.userList.find((u) => u.id === userId);
    return user ? user.name : ' ';
  }

  gotoAdmin() {
    this.router.navigate(['/admin'], {
      queryParams: { exhibit: this.exhibitId },
    });
  }

  gotoSection(sectionOrCardId: string) {
    switch (sectionOrCardId) {
      case 'wall':
      case 'archive':
        this.selectedSection = sectionOrCardId;
        this.cardDataService.setActive('');
        this.uiDataService.setSection(this.exhibitId, sectionOrCardId);
        this.router.navigate([], {
          relativeTo: this.activatedRoute,
          queryParams: { exhibit: this.exhibitId, section: sectionOrCardId },
          queryParamsHandling: 'merge'
        });
        break;
      case 'admin':
        this.gotoAdmin();
        break;
      default:
        this.selectedSection = Section.archive;
        this.cardDataService.setActive(sectionOrCardId);
        this.uiDataService.setSection(this.exhibitId, this.selectedSection);
        this.router.navigate([], {
          relativeTo: this.activatedRoute,
          queryParams: { exhibit: this.exhibitId, section: this.selectedSection, card: sectionOrCardId },
          queryParamsHandling: 'merge'
        });
        break;
    }
  }

  applyFilter(filterValue: string) {
    console.log('applyFilter: ' + filterValue);
    this.filterString = filterValue;
    const filter = this.filterString?.toLowerCase() ?? '';
    this.exhibitList.data = this.allExhibits.filter((exhibit) => {
      const name = exhibit.name?.toLowerCase() ?? '';
      const collection = this.getCollectionName(exhibit.collectionId)?.toLowerCase() ?? '';
      const createdBy = this.getUserName(exhibit.createdBy)?.toLowerCase() ?? '';
      return name.includes(filter) || collection.includes(filter) || createdBy.includes(filter);
    });
  }

  onFilterChange() {
    console.log('filterChange: ' + this.filterString);
    if (!this.filterString) {
      this.clearFilter();
    }
  }

  clearFilter() {
    this.applyFilter('');
  }

  topBarNavigate(url): void {
    this.router.navigate(['/']).then(() => {
      window.location.reload();
    });
  }

  ngOnDestroy() {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }
}
